import { useCallback, useEffect, useMemo, useState } from "react";
import { api, idr } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, Wallet, Search, Ban, Paperclip } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { hariIniISO, formatTanggal } from "../lib/tanggal";
import InputRupiah from "../components/InputRupiah";

// Down Payment (uang muka pelanggan) — brief owner 29 Agu 2026.
//
// DP adalah uang yang masuk SEBELUM ada notanya: kewajiban, bukan penjualan.
// Halaman ini mencatat penerimaannya; pemakaiannya terjadi di Input Nota
// (pilih DP → sisa pembayaran berkurang otomatis).

const STATUS_LABEL = {
  belum_digunakan: { l: "Belum Digunakan", c: "bg-sky-50 text-sky-700 border-sky-200" },
  sebagian_digunakan: { l: "Sebagian Digunakan", c: "bg-amber-50 text-amber-700 border-amber-200" },
  sudah_digunakan: { l: "Sudah Digunakan", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  batal: { l: "Dibatalkan", c: "bg-slate-100 text-slate-500 border-slate-200" },
};

function StatusPill({ s }) {
  const m = STATUS_LABEL[s] || { l: s, c: "bg-slate-50 text-slate-600 border-slate-200" };
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium border ${m.c}`}>{m.l}</span>;
}

/**
 * Pembungkus label + kolom isian form DP.
 *
 * HARUS di ruang modul, JANGAN dipindah ke dalam `FormDP` (brief owner 12 Sep,
 * poin "kursor tetap aktif"): komponen yang didefinisikan di dalam badan induk
 * lahir sebagai TIPE BARU setiap render. React melihat tipe yang berbeda, jadi
 * ia tidak memperbarui subtree-nya melainkan MEMBONGKAR lalu memasang ulang —
 * node `<input>`-nya diganti, dan fokus beserta posisi kursor ikut hilang.
 * Akibatnya kasir harus mengklik ulang kolomnya setiap satu huruf.
 *
 * Komponen ini sengaja tidak menutup (closure) atas satu pun nilai milik
 * `FormDP` — hanya membaca propnya sendiri — sehingga aman berada di sini.
 */
function Kolom({ label, children, wajib }) {
  return (
    <div>
      <label className="block text-[11px] font-medium text-slate-600 mb-1">
        {label}{wajib && <span className="text-rose-600"> *</span>}
      </label>
      {children}
    </div>
  );
}

export default function DownPayment() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [ringkas, setRingkas] = useState({});
  const [loading, setLoading] = useState(true);
  const [cari, setCari] = useState("");
  const [cariAktif, setCariAktif] = useState("");
  const [status, setStatus] = useState("");
  const [dari, setDari] = useState("");
  const [sampai, setSampai] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [detail, setDetail] = useState(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setCariAktif(cari.trim()), 350);
    return () => clearTimeout(t);
  }, [cari]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (cariAktif) p.set("q", cariAktif);
      if (status) p.set("status", status);
      if (dari) p.set("dari", dari);
      if (sampai) p.set("sampai", sampai);
      const r = await api.get(`/down-payment${p.toString() ? "?" + p.toString() : ""}`);
      setItems(r.data.items || []);
      setRingkas(r.data);
    } catch (e) {
      const pesan = e?.response?.data?.detail;
      toast.error(e?.response?.status === 400 && pesan ? pesan : "Gagal memuat DP");
    } finally { setLoading(false); }
  }, [cariAktif, status, dari, sampai]);
  useEffect(() => { load(); }, [load, reload]);

  const batalkan = async (d) => {
    const alasan = window.prompt(
      `Batalkan DP ${d.code} Rp ${Math.round(d.nominal).toLocaleString("id-ID")}?\n\n` +
      "Jurnalnya akan DIBALIK dengan entri lawan (bukan dihapus), karena uangnya " +
      "memang pernah diterima dan sudah tercermin di saldo kas.\n\nAlasan pembatalan:");
    if (alasan === null) return;
    if (!alasan.trim()) { toast.error("Alasan pembatalan wajib diisi"); return; }
    try {
      await api.post(`/down-payment/${d.id}/batal`, { alasan });
      toast.success(`DP ${d.code} dibatalkan`);
      setReload(n => n + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membatalkan DP"); }
  };

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="down-payment-page">
      <div className="flex items-start justify-between flex-col md:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">POS &amp; Sales</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1 inline-flex items-center gap-2">
            <Wallet className="w-6 h-6 text-emerald-600" /> Down Payment
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Uang muka pelanggan sebelum notanya dibuat. Dipakai saat membuat nota di Input Nota.
          </p>
        </div>
        {can("down_payment", "create") && (
          <button onClick={() => setShowForm(true)} data-testid="dp-new"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium">
            <Plus className="w-4 h-4" /> Terima DP
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="dp-kartu">
        {[
          { l: "Total DP Diterima", v: idr(ringkas.total_nominal || 0), s: `${items.length} transaksi` },
          { l: "Sudah Dipakai", v: idr(ringkas.total_terpakai || 0), s: "menutup nota penjualan" },
          { l: "Sisa Belum Dipakai", v: idr(ringkas.total_sisa || 0), s: "masih jadi kewajiban" },
        ].map(k => (
          <div key={k.l} className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
            <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{k.l}</p>
            <p className="font-display text-xl font-bold text-slate-900 mt-1">{k.v}</p>
            <p className="text-[11px] text-slate-500 mt-0.5">{k.s}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-3 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <label className="block text-[11px] font-medium text-slate-500 mb-1">Cari</label>
          <div className="flex items-center gap-2 h-10 px-3 rounded-lg border border-slate-200">
            <Search className="w-4 h-4 text-slate-400 shrink-0" />
            <input value={cari} onChange={e => setCari(e.target.value)} data-testid="dp-cari"
              placeholder="Nama, no HP, atau kode DP..." className="flex-1 text-sm outline-none bg-transparent" />
          </div>
        </div>
        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">Status</label>
          <select value={status} onChange={e => setStatus(e.target.value)} data-testid="dp-status"
            className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
            <option value="">Semua status</option>
            <option value="belum_digunakan">Belum Digunakan</option>
            <option value="sebagian_digunakan">Sebagian Digunakan</option>
            <option value="sudah_digunakan">Sudah Digunakan</option>
            <option value="batal">Dibatalkan</option>
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">Dari</label>
          <input type="date" value={dari} onChange={e => setDari(e.target.value)} data-testid="dp-dari"
            className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">Sampai</label>
          <input type="date" value={sampai} onChange={e => setSampai(e.target.value)} data-testid="dp-sampai"
            className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
        </div>
        {(cari || status || dari || sampai) && (
          <button onClick={() => { setCari(""); setStatus(""); setDari(""); setSampai(""); }}
            data-testid="dp-reset"
            className="h-10 px-3 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm">Reset</button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-slate-50 border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2.5 text-left">Kode / Tanggal</th>
                <th className="px-4 py-2.5 text-left">Customer</th>
                <th className="px-4 py-2.5 text-left">Cabang / Metode</th>
                <th className="px-4 py-2.5 text-right">Nominal</th>
                <th className="px-4 py-2.5 text-right">Terpakai</th>
                <th className="px-4 py-2.5 text-right">Sisa</th>
                <th className="px-4 py-2.5 text-center">Status</th>
                <th className="px-4 py-2.5 text-left">PIC</th>
                <th className="px-4 py-2.5 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="p-10 text-center text-sm text-slate-400">Memuat...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={9} className="p-10 text-center text-sm text-slate-400" data-testid="dp-kosong">
                  Belum ada DP yang cocok dengan filter ini
                </td></tr>
              ) : items.map(d => (
                <tr key={d.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`dp-row-${d.id}`}>
                  <td className="px-4 py-3">
                    <p className="font-mono text-xs text-slate-500">{d.code}</p>
                    <p className="text-xs text-slate-600">{formatTanggal(d.tanggal)}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-900">{d.pelanggan_nama}</p>
                    {/* No HP adalah NOMOR REFERENSI transaksi — dipakai mencari
                        DP-nya saat notanya dibuat. */}
                    <p className="text-[10px] text-slate-500 font-mono">{d.no_hp}</p>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {d.brand || "-"}<span className="block text-[10px] text-slate-400">{d.metode_pembayaran}</span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-800">{idr(d.nominal)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-500">{idr(d.terpakai)}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">{idr(d.sisa)}</td>
                  <td className="px-4 py-3 text-center"><StatusPill s={d.status} /></td>
                  <td className="px-4 py-3 text-xs text-slate-600">{d.pic || "-"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => setDetail(d)} data-testid={`dp-detail-${d.id}`}
                      className="text-xs font-medium text-sky-700 hover:underline mr-2">Riwayat</button>
                    {can("down_payment", "delete") && d.status === "belum_digunakan" && (
                      <button onClick={() => batalkan(d)} data-testid={`dp-batal-${d.id}`}
                        className="text-xs font-medium text-rose-700 hover:underline inline-flex items-center gap-1">
                        <Ban className="w-3 h-3" /> Batal
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && <FormDP onClose={(ok) => { setShowForm(false); if (ok) setReload(n => n + 1); }} />}
      {detail && <RiwayatDP dp={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function FormDP({ onClose }) {
  const [form, setForm] = useState({
    pelanggan_nama: "", no_hp: "", tanggal: hariIniISO(), nominal: "",
    akun_id: "", brand: "", metode_pembayaran: "Cash", keterangan: "",
  });
  const [bukti, setBukti] = useState(null);
  const [akun, setAkun] = useState([]);
  const [cabang, setCabang] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/finance/accounts/kas-bank").then(r => {
      const a = r.data || [];
      setAkun(a);
      if (a.length) setForm(f => ({ ...f, akun_id: f.akun_id || a[0].id }));
    }).catch(() => toast.error("Gagal memuat akun penerimaan"));
    // Endpoint cabang yang SAMA dengan halaman lain (Master, Pre-Order,
    // Loss Customer) — bukan daftar kedua yang bisa berpisah ejaan.
    api.get("/finance/branches/master")
      .then(r => setCabang((r.data?.items || r.data || []).map(b => b.name || b)))
      .catch(() => { /* dropdown cabang tetap kosong; DP boleh tanpa cabang */ });
  }, []);

  const pilihBukti = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // 2 MB: bukti transfer adalah tangkapan layar, bukan foto resolusi penuh.
    // Tanpa batas, satu unggahan besar membuat dokumen DP-nya berat dibaca
    // di setiap daftar.
    if (f.size > 2 * 1024 * 1024) { toast.error("Bukti maksimal 2 MB"); return; }
    const fr = new FileReader();
    fr.onload = () => setBukti(fr.result);
    fr.readAsDataURL(f);
  };

  const simpan = async () => {
    if (!form.pelanggan_nama.trim()) { toast.error("Nama customer wajib diisi"); return; }
    if (!form.no_hp.trim()) { toast.error("Nomor HP wajib — dipakai sebagai nomor referensi DP"); return; }
    if (!Number(form.nominal)) { toast.error("Nominal DP wajib diisi"); return; }
    setBusy(true);
    try {
      const r = await api.post("/down-payment", {
        ...form, nominal: Number(form.nominal),
        brand: form.brand || null, bukti_base64: bukti,
      });
      toast.success(r.data?.message || "DP tercatat");
      onClose(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan DP");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => onClose(false)}>
      <div onClick={e => e.stopPropagation()} data-testid="dp-form"
           className="bg-white rounded-2xl max-w-lg w-full max-h-[88vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">Terima Down Payment</h3>
          <button onClick={() => onClose(false)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 grid grid-cols-2 gap-3">
          <Kolom label="Nama Customer" wajib>
            <input value={form.pelanggan_nama} data-testid="dp-nama"
              onChange={e => setForm({ ...form, pelanggan_nama: e.target.value })}
              className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
          </Kolom>
          <Kolom label="Nomor HP (nomor referensi)" wajib>
            <input value={form.no_hp} data-testid="dp-hp"
              onChange={e => setForm({ ...form, no_hp: e.target.value })}
              className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm font-mono" />
          </Kolom>
          <Kolom label="Tanggal DP" wajib>
            <input type="date" value={form.tanggal} data-testid="dp-tanggal"
              onChange={e => setForm({ ...form, tanggal: e.target.value })}
              className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
          </Kolom>
          <Kolom label="Nominal DP" wajib>
            <InputRupiah value={form.nominal} data-testid="dp-nominal"
              onChange={v => setForm({ ...form, nominal: v })}
              className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
          </Kolom>
          <Kolom label="Diterima di Akun" wajib>
            <select value={form.akun_id} data-testid="dp-akun"
              onChange={e => setForm({ ...form, akun_id: e.target.value })}
              className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
              {akun.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </Kolom>
          <Kolom label="Cabang">
            <select value={form.brand} data-testid="dp-cabang"
              onChange={e => setForm({ ...form, brand: e.target.value })}
              className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
              <option value="">— pilih cabang —</option>
              {cabang.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </Kolom>
          <Kolom label="Metode Pembayaran">
            <select value={form.metode_pembayaran} data-testid="dp-metode"
              onChange={e => setForm({ ...form, metode_pembayaran: e.target.value })}
              className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
              <option>Cash</option><option>Transfer</option><option>QRIS</option><option>Debit</option>
            </select>
          </Kolom>
          <Kolom label="Bukti Pembayaran">
            <label className="h-10 px-3 rounded-lg border border-dashed border-slate-300 text-xs text-slate-600 flex items-center gap-2 cursor-pointer hover:bg-slate-50">
              <Paperclip className="w-3.5 h-3.5" />
              {bukti ? "Bukti terpasang" : "Pilih gambar"}
              <input type="file" accept="image/*" onChange={pilihBukti} data-testid="dp-bukti" className="hidden" />
            </label>
          </Kolom>
          <div className="col-span-2">
            <Kolom label="Keterangan">
              <textarea value={form.keterangan} rows={2} data-testid="dp-keterangan"
                onChange={e => setForm({ ...form, keterangan: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            </Kolom>
          </div>
          <p className="col-span-2 text-[11px] text-slate-500">
            Uang muka dicatat sebagai <b>kewajiban</b> (COA 2160), bukan pendapatan — barangnya
            belum diserahkan. Ia baru berkurang saat DP ini dipakai menutup sebuah nota.
          </p>
        </div>
        <div className="p-5 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={() => onClose(false)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
          <button onClick={simpan} disabled={busy} data-testid="dp-simpan"
            className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium">
            {busy ? "Menyimpan..." : "Simpan DP"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RiwayatDP({ dp, onClose }) {
  const [d, setD] = useState(dp);
  useEffect(() => {
    api.get(`/down-payment/${dp.id}`).then(r => setD(r.data)).catch(() => {});
  }, [dp.id]);
  const riwayat = useMemo(() => [...(d.riwayat || [])].reverse(), [d]);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="dp-riwayat"
           className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-display font-bold text-lg">Riwayat DP {d.code}</h3>
            <p className="text-xs text-slate-500">{d.pelanggan_nama} · {d.no_hp}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div><p className="text-[11px] text-slate-500">Nominal</p><p className="font-mono">{idr(d.nominal)}</p></div>
            <div><p className="text-[11px] text-slate-500">Terpakai</p><p className="font-mono">{idr(d.terpakai)}</p></div>
            <div><p className="text-[11px] text-slate-500">Sisa</p><p className="font-mono font-semibold">{idr(d.sisa)}</p></div>
          </div>
          {d.batal && (
            <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
              Dibatalkan oleh {d.batal_by}: {d.batal_alasan}
            </p>
          )}
          <div>
            <p className="text-[11px] font-semibold uppercase text-slate-500 mb-1">Dipakai di nota</p>
            {riwayat.length === 0 ? (
              <p className="text-xs text-slate-400 py-3">Belum pernah dipakai di nota mana pun.</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-1.5 px-2">Nota</th>
                    <th className="text-left py-1.5 px-2">Tanggal</th>
                    <th className="text-right py-1.5 px-2">Nominal</th>
                    <th className="text-left py-1.5 px-2">Oleh</th>
                  </tr>
                </thead>
                <tbody>
                  {riwayat.map((h, i) => (
                    <tr key={i} className="border-b border-slate-100 last:border-0">
                      <td className="py-1.5 px-2 font-mono">{h.sale_code || h.sale_id?.slice(0, 8)}</td>
                      <td className="py-1.5 px-2">{formatTanggal(h.tanggal)}</td>
                      <td className="py-1.5 px-2 text-right font-mono">{idr(h.nominal)}</td>
                      <td className="py-1.5 px-2 text-slate-600">{h.by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {d.bukti_base64 && (
            <div>
              <p className="text-[11px] font-semibold uppercase text-slate-500 mb-1">Bukti Pembayaran</p>
              <img src={d.bukti_base64} alt="bukti" className="max-h-64 rounded-lg border border-slate-200" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
